import { Controller } from "@hotwired/stimulus";
import warnLeavingUnsaved from "@/src/utils/warnLeavingUnsaved";
import { redirectToUrl } from "@/src/utils/safeNavigation";

const FIELD_SELECTOR = 'input[name^="easy_money_rates["]';
const PERIOD_SELECT = "easy8-ds-select[data-name='valid_from']";
const ALWAYS = "always";

const elementOf = (event: Event): HTMLElement | null => (event.target instanceof HTMLElement ? event.target : null);

const controlName = (event: Event): string | undefined =>
  elementOf(event)?.closest<HTMLElement>("[data-name]")?.dataset.name;

/** "save" | "discard" | null — which footer button was clicked. */
const footerButtonAction = (event: Event): string | null => {
  const cy = elementOf(event)?.closest<HTMLElement>("[data-cy]")?.dataset.cy ?? "";
  const match = cy.match(/-button--(save|discard)$/);

  return match ? match[1] : null;
};

export default class extends Controller<HTMLElement> {
  static values = { saveDialogUrl: String, tabUrl: String, period: String };
  static targets = ["dialogFrame", "affectedFrame", "scopeWarning"];

  declare readonly saveDialogUrlValue: string;
  declare readonly tabUrlValue: string;
  // The period the server rendered. The URL need not name it, so it is not a source of truth here.
  declare readonly periodValue: string;
  declare readonly dialogFrameTarget: HTMLElement;
  declare readonly affectedFrameTarget: HTMLElement;
  declare readonly hasAffectedFrameTarget: boolean;
  // One per scope that overwrites rates other projects own; each carries the scope it belongs to.
  declare readonly scopeWarningTargets: HTMLElement[];

  // A row is the unit of saving: UpdateRates reads easy_currency_code once per entity.
  private readonly dirty = new Set<HTMLTableRowElement>();
  private saveScope = "";

  connect(): void {
    // Capture phase: a DS field consumes the inner input's event before it bubbles out.
    this.element.addEventListener("input", this.onFieldEvent, true);
    this.element.addEventListener("change", this.onFieldEvent, true);
    this.element.addEventListener("click", this.onSelectClick, true);

    // The callback list cannot be unregistered, so capture only the set, never `this`.
    const dirty = this.dirty;
    warnLeavingUnsaved.addForcedFormChangedCallback(() => dirty.size > 0);
  }

  disconnect(): void {
    this.element.removeEventListener("input", this.onFieldEvent, true);
    this.element.removeEventListener("change", this.onFieldEvent, true);
    this.element.removeEventListener("click", this.onSelectClick, true);

    // The registered callback still holds this set, so a stale grid would keep warning after navigation.
    this.dirty.clear();
  }

  // ds_action_buttons renders its buttons from a JSON prop, so the footer is dispatched by data-cy.
  footerAction(event: Event): void {
    const cy = footerButtonAction(event);

    if (cy === "save") this.openSaveDialog(event);
    if (cy === "discard") this.discard(event);
  }

  openSaveDialog(event: Event): void {
    event.preventDefault();

    const url = new URL(this.saveDialogUrlValue, window.location.origin);

    this.dialogFrameTarget.removeAttribute("src");
    this.dialogFrameTarget.setAttribute("src", url.toString());
  }

  discard(event: Event): void {
    event.preventDefault();
    this.dirty.clear();
    window.location.reload();
  }

  // The browser builds the entry list after the submit event, so rows appended here are posted.
  appendChanges(event: Event): void {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    this.dirty.forEach(row => {
      row.querySelectorAll<HTMLInputElement>(FIELD_SELECTOR).forEach(input => {
        const field = document.createElement("input");
        field.type = "hidden";
        field.name = input.name;
        field.value = input.value;
        form.appendChild(field);
      });
    });

    this.dirty.clear();
  }

  // The list lives in a collapsed fieldset, so it can appear long after the scope was chosen.
  affectedFrameTargetConnected(): void {
    this.syncAffectedFrame();
  }

  // Marks the whole row; an untouched value is a server-side no-op.
  private onFieldEvent = (event: Event): void => {
    const name = controlName(event);
    if (!name) return;

    if (name === "save_setting") this.scopeChanged(event);
    else if (name.startsWith("easy_money_rates[")) this.markDirty(elementOf(event)?.closest("tr"));
  };

  // Choosing an option writes only the hidden input, with no event, so it is read back after the click.
  private onSelectClick = (event: Event): void => {
    const select = elementOf(event)?.closest<HTMLElement>("easy8-ds-select");
    if (!select) return;

    requestAnimationFrame(() => {
      if (select.matches(PERIOD_SELECT)) this.switchPeriod(select);
      else this.markDirty(select.closest("tr"));
    });
  };

  private switchPeriod(select: HTMLElement): void {
    const value = select.querySelector<HTMLInputElement>('input[name="valid_from"]')?.value;
    if (!value || value === this.periodValue) return;

    const url = new URL(this.tabUrlValue, window.location.origin);
    if (value === ALWAYS) {
      url.searchParams.set("valid_from_type", ALWAYS);
    } else {
      url.searchParams.set("valid_from_type", "date");
      url.searchParams.set("valid_from", value);
    }

    redirectToUrl(url.toString());
  }

  private scopeChanged(event: Event): void {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement)) return;

    this.saveScope = radio.value;
    this.scopeWarningTargets.forEach(warning => {
      warning.hidden = warning.dataset.scope !== this.saveScope;
    });

    this.syncAffectedFrame();
  }

  private syncAffectedFrame(): void {
    if (!this.saveScope || !this.hasAffectedFrameTarget) return;

    const source = this.affectedFrameTarget.getAttribute("src");
    if (!source) return;

    const url = new URL(source, window.location.origin);
    if (url.searchParams.get("type") === this.saveScope) return;

    url.searchParams.set("type", this.saveScope);
    // A different scope is a different list, so the page the user had reached no longer means anything.
    url.searchParams.delete("page");
    this.affectedFrameTarget.setAttribute("src", url.toString());
  }

  private markDirty(row: Element | null | undefined): void {
    if (!(row instanceof HTMLTableRowElement) || this.dirty.has(row)) return;

    if (row.querySelector(FIELD_SELECTOR)) this.dirty.add(row);
  }
}
